import { Box, Flex, HStack, Menu, Text } from '@chakra-ui/react';
import { useClerk } from '@clerk/clerk-react';
import { Calculator, Cog, LogOut, Settings, User } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppHeader } from '../shared/AppHeaderContext';

export const Header = () => {
  const { openUserProfile, signOut } = useClerk();
  const navigate = useNavigate();
  const { title, leftActions, rightActions } = useAppHeader();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleSignOut = async () => {
    setMenuOpen(false);
    await signOut();
  };

  const handleNavigate = (path: string) => {
    setMenuOpen(false);
    navigate(path);
  };

  const handleOpenUserProfile = () => {
    setMenuOpen(false);
    openUserProfile();
  };

  return (
    <Box
      position="sticky"
      top={0}
      bg="neutral.surface"
      borderBottom="1px solid"
      borderColor="neutral.border"
      zIndex={1000}
    >
      <Box px={6}>
        <Flex py={4} alignItems="center" gap={4}>
          {/* Left Section: Logo/Home + Page Navigation */}
          <HStack gap={4} flex={1}>
            {/* Logo/Home - Always visible, click to go to dashboard */}
            <HStack
              gap={2}
              cursor="pointer"
              onClick={() => navigate('/')}
              _hover={{ opacity: 0.8 }}
              title="Go to Dashboard"
              flexShrink={0}
            >
              <Calculator size={24} color="var(--chakra-colors-blue-600)" />
              <Text
                fontSize="lg"
                fontWeight="bold"
                color="blue.600"
                display={{ base: 'none', md: 'block' }}
              >
                Bond Calculator
              </Text>
            </HStack>

            {/* Page-specific navigation (back buttons, etc.) */}
            {leftActions}
          </HStack>

          {/* Center Section: Page Title */}
          <Box flex={2} textAlign="center">
            <Text
              fontSize="h2"
              fontWeight="bold"
              color="black"
              maxWidth="400px"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              mx="auto"
              title={title}
            >
              {title}
            </Text>
          </Box>

          {/* Right Section: Page Actions + User Menu */}
          <HStack gap={2} flex={1} justify="flex-end">
            {rightActions}

            {/* Profile Menu - Controlled with unmount to prevent ghost clicks */}
            <Menu.Root
              open={menuOpen}
              onOpenChange={(e) => setMenuOpen(e.open)}
              lazyMount
              unmountOnExit
              closeOnSelect
              positioning={{ strategy: 'fixed' }}
            >
              <Menu.Trigger asChild>
                <Box
                  as="button"
                  p={2}
                  borderRadius="md"
                  color="neutral.textPrimary"
                  _hover={{ bg: 'gray.100' }}
                  cursor="pointer"
                >
                  <User size={24} />
                </Box>
              </Menu.Trigger>
              <Menu.Positioner>
                <Menu.Content minW="200px" display={menuOpen ? 'block' : 'none'}>
                  <Menu.Item value="settings" onClick={() => handleNavigate('/settings')}>
                    <Cog size={16} />
                    App Settings
                  </Menu.Item>
                  <Menu.Item value="account" onClick={handleOpenUserProfile}>
                    <Settings size={16} />
                    Account Settings
                  </Menu.Item>
                  <Menu.Separator />
                  <Menu.Item value="logout" onClick={handleSignOut}>
                    <LogOut size={16} />
                    Logout
                  </Menu.Item>
                </Menu.Content>
              </Menu.Positioner>
            </Menu.Root>
          </HStack>
        </Flex>
      </Box>
    </Box>
  );
};
